import { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { UsersIcon, ClipboardDocumentListIcon, BuildingOfficeIcon, BookOpenIcon, TagIcon, PencilIcon, TrashIcon, LinkIcon, PowerIcon } from '@heroicons/react/24/outline';
import { mediaUrl } from '../utils/media';
import { exportCsv } from '../utils/exportCsv';

const PAGE_SIZE = 10;

const TABS = [
  { key: 'users', label: 'Pengguna', icon: UsersIcon },
  { key: 'orders', label: 'Pesanan', icon: ClipboardDocumentListIcon },
  { key: 'tenants', label: 'Tenant', icon: BuildingOfficeIcon },
  { key: 'ebooks', label: 'Ebook', icon: BookOpenIcon },
  { key: 'vouchers', label: 'Voucher', icon: TagIcon },
  { key: 'affiliate', label: 'Affiliate', icon: LinkIcon },
];

const ROLES = ['user', 'superadmin'];
const STATUS_COLOR = { pending: 'text-yellow-600', waiting_confirm: 'text-blue-600', paid: 'text-green-600', failed: 'text-red-500', expired: 'text-gray-400' };
const fmtRp = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

function extractItems(resp) { return resp.data?.items ?? resp.data ?? []; }

const EMPTY_VOUCHER = { code: '', type: 'percent', value: '', min_amount: '', max_uses: '', description: '', valid_from: '', valid_until: '' };

export default function AdminPage() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [voucherForm, setVoucherForm] = useState({ ...EMPTY_VOUCHER });
  const [editingVoucher, setEditingVoucher] = useState(null);

  // ── Filter & paging state ──
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [tab, search]);

  const fetchAll = () => {
    api.get('/api/admin/users').then((r) => setUsers(extractItems(r))).catch(() => toast.error('Gagal memuat pengguna'));
    api.get('/api/admin/orders').then((r) => setOrders(extractItems(r))).catch(() => toast.error('Gagal memuat pesanan'));
    api.get('/api/admin/tenants').then((r) => setTenants(extractItems(r))).catch(() => {});
    api.get('/api/admin/ebooks').then((r) => setEbooks(extractItems(r))).catch(() => {});
    api.get('/api/admin/vouchers').then((r) => setVouchers(extractItems(r))).catch(() => {});
  };

  useEffect(fetchAll, []);

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/api/admin/users/${userId}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
      toast.success('Role diperbarui');
    } catch { toast.error('Gagal mengubah role'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { type, data } = editing;
    try {
      if (type === 'user') {
        await api.put(`/api/admin/users/${data.id}`, { name: data.name, email: data.email, role: data.role });
      } else if (type === 'tenant') {
        await api.put(`/api/admin/tenants/${data.id}`, { name: data.name, plan: data.plan, is_active: data.is_active });
      } else if (type === 'ebook') {
        await api.put(`/api/admin/ebooks/${data.id}/toggle`, {});
        toast.success('Status ebook diubah');
      } else if (type === 'order') {
        await api.put(`/api/admin/orders/${data.id}/status`, { status: data.status });
      }
      toast.success('Tersimpan');
      setEditing(null);
      fetchAll();
    } catch { toast.error('Gagal menyimpan'); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const { type, id } = confirmDel;
    try {
      await api.delete(`/api/admin/${type}s/${id}`);
      toast.success('Dihapus');
      setConfirmDel(null);
      fetchAll();
    } catch { toast.error('Gagal menghapus'); }
  };

  const openEdit = (type, data) => {
    if (type === 'ebook') {
      api.put(`/api/admin/ebooks/${data.id}/toggle`, {}).then(() => { toast.success('Status diubah'); fetchAll(); }).catch(() => toast.error('Gagal'));
      return;
    }
    setEditing({ type, data: { ...data } });
  };

  const TrashBtn = ({ type, id, label }) => (
    <button onClick={() => setConfirmDel({ type, id, label })} className="text-red-500 hover:text-red-700 p-1">
      <TrashIcon className="w-4 h-4" />
    </button>
  );

  // ── Voucher CRUD ──
  const saveVoucher = async () => {
    const payload = {
      code: voucherForm.code.toUpperCase().trim(),
      type: voucherForm.type,
      value: Number(voucherForm.value),
      min_amount: Number(voucherForm.min_amount) || 0,
      max_uses: Number(voucherForm.max_uses) || 0,
      description: voucherForm.description || undefined,
      valid_from: voucherForm.valid_from || undefined,
      valid_until: voucherForm.valid_until || undefined,
    };
    if (!payload.code || !payload.value) { toast.error('Kode & nilai wajib'); return; }
    try {
      if (editingVoucher) {
        await api.put(`/api/admin/vouchers/${editingVoucher.id}`, payload);
        toast.success('Voucher diperbarui');
      } else {
        await api.post('/api/admin/vouchers', payload);
        toast.success('Voucher dibuat');
      }
      setShowVoucherForm(false);
      setEditingVoucher(null);
      setVoucherForm({ ...EMPTY_VOUCHER });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal menyimpan voucher'); }
  };

  const toggleVoucher = async (id) => {
    try {
      await api.post(`/api/admin/vouchers/${id}/toggle`);
      toast.success('Status diubah');
      fetchAll();
    } catch { toast.error('Gagal toggle'); }
  };

  const openEditVoucher = (v) => {
    setEditingVoucher(v);
    setVoucherForm({
      code: v.code,
      type: v.type,
      value: v.value,
      min_amount: v.min_amount || '',
      max_uses: v.max_uses || '',
      description: v.description || '',
      valid_from: v.valid_from ? v.valid_from.split('T')[0] : '',
      valid_until: v.valid_until ? v.valid_until.split('T')[0] : '',
    });
    setShowVoucherForm(true);
  };

  // ── Filter, paging, export helpers ──
  const filterRows = (rows, keys) => {
    const q = search.toLowerCase();
    return q ? rows.filter((r) => keys.some((k) => String(r[k] || '').toLowerCase().includes(q))) : rows;
  };
  const paginate = (arr) => arr.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = (n) => Math.max(1, Math.ceil(n / PAGE_SIZE));

  const Paging = ({ total }) => {
    const pages = totalPages(total);
    if (pages <= 1) return null;
    return (
      <div className="flex items-center justify-between p-4 border-t text-sm">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 rounded border text-xs disabled:opacity-40">Prev</button>
        <span className="text-gray-500 text-xs">Halaman {page} dari {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded border text-xs disabled:opacity-40">Next</button>
      </div>
    );
  };

  const Toolbar = ({ filtered, exportName, exportHeaders, exportMap }) => (
    <div className="p-4 border-b flex flex-wrap items-center gap-2">
      <input value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Cari..."
        className="flex-1 min-w-[150px] border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      <button onClick={() => exportCsv(exportName + '.csv', filtered.map(exportMap), exportHeaders)}
        className="bg-green-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-green-700 transition">
        Export CSV
      </button>
      <span className="text-xs text-gray-500 whitespace-nowrap">{filtered.length} data</span>
    </div>
  );

  // Filtered + paginated datasets
  const fUsers = filterRows(users, ['name', 'email', 'role']);
  const fOrders = filterRows(orders, ['id', 'ebook_title', 'status', 'ebook_id']);
  const fTenants = filterRows(tenants, ['name', 'owner_name', 'owner_email', 'plan']);
  const fEbooks = filterRows(ebooks, ['title', 'description']);
  const fVouchers = filterRows(vouchers, ['code', 'description']);

  return (
    <div className="flex flex-col h-screen bg-wa-panel">
      <Navbar title="Admin Panel" />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-white overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === key ? 'border-wa-dark text-wa-dark' : 'border-transparent text-gray-500 hover:text-wa-dark'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* ── USERS ── */}
        {tab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b"><h2 className="font-semibold text-wa-text">Pengguna</h2></div>
            <Toolbar filtered={fUsers} exportName="pengguna"
              exportHeaders={['Nama', 'Email', 'Role']}
              exportMap={(u) => ({ 'Nama': u.name, 'Email': u.email, 'Role': u.role })} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Aksi</th>
                </tr></thead>
                <tbody>
                  {paginate(fUsers).map((u) => (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-gray-500">{u.email}</td>
                      <td className="p-3">
                        <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                          className="border border-gray-200 rounded px-2 py-1 text-xs bg-white">
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className="p-3"><button onClick={() => openEdit('user', u)} className="text-wa-dark hover:text-wa-green p-1" title="Edit"><PencilIcon className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fUsers.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Tidak ada pengguna</p>}
            </div>
            <Paging total={fUsers.length} />
          </div>
        )}

        {/* ── ORDERS ── */}
        {tab === 'orders' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b"><h2 className="font-semibold text-wa-text">Pesanan</h2></div>
            <Toolbar filtered={fOrders} exportName="pesanan"
              exportHeaders={['ID', 'Ebook', 'Jumlah', 'Status']}
              exportMap={(o) => ({ 'ID': o.id, 'Ebook': o.ebook_title || o.ebook_id, 'Jumlah': o.amount, 'Status': o.status })} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="p-3">ID</th><th className="p-3">Ebook</th><th className="p-3">Jumlah</th><th className="p-3">Status</th><th className="p-3">Aksi</th>
                </tr></thead>
                <tbody>
                  {paginate(fOrders).map((o) => (
                    <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-3 font-mono text-xs">{o.id?.slice(-8)}</td>
                      <td className="p-3">{o.ebook_title || o.ebook_id?.slice(-8)}</td>
                      <td className="p-3">{fmtRp(o.amount)}</td>
                      <td className="p-3">
                        <span className={`font-medium text-xs uppercase ${STATUS_COLOR[o.status] || ''}`}>{o.status === 'waiting_confirm' ? 'Menunggu Konfirmasi' : o.status}</span>
                      </td>
                      <td className="p-3 flex items-center gap-1">
                        {o.status === 'waiting_confirm' && (
                          <button
                            onClick={async () => {
                              await api.put(`/api/admin/orders/${o.id}`, { status: 'paid' });
                              toast.success('Order dikonfirmasi');
                              fetchAll();
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 rounded"
                            title="Approve pembayaran"
                          >✓ Approve</button>
                        )}
                        <button onClick={() => openEdit('order', o)} className="text-wa-dark hover:text-wa-green p-1" title="Edit"><PencilIcon className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fOrders.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Tidak ada pesanan</p>}
            </div>
            <Paging total={fOrders.length} />
          </div>
        )}

        {/* ── TENANTS ── */}
        {tab === 'tenants' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b"><h2 className="font-semibold text-wa-text">Tenant</h2></div>
            <Toolbar filtered={fTenants} exportName="tenant"
              exportHeaders={['Nama', 'Owner', 'Plan', 'Status']}
              exportMap={(t) => ({ 'Nama': t.name, 'Owner': t.owner_name || t.owner_email, 'Plan': t.plan, 'Status': t.is_active ? 'Aktif' : 'Nonaktif' })} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="p-3">Nama</th><th className="p-3">Owner</th><th className="p-3">Plan</th><th className="p-3">Status</th><th className="p-3">Aksi</th>
                </tr></thead>
                <tbody>
                  {paginate(fTenants).map((t) => (
                    <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-3 font-medium">{t.name}</td>
                      <td className="p-3 text-gray-500 text-xs">{t.owner_name || t.owner_email}</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">{t.plan}</span></td>
                      <td className="p-3"><span className={`text-xs ${t.is_active ? 'text-green-600' : 'text-gray-400'}`}>{t.is_active ? 'Aktif' : 'Nonaktif'}</span></td>
                      <td className="p-3"><button onClick={() => openEdit('tenant', t)} className="text-wa-dark hover:text-wa-green p-1" title="Edit"><PencilIcon className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fTenants.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Tidak ada tenant</p>}
            </div>
            <Paging total={fTenants.length} />
          </div>
        )}

        {/* ── EBOOKS ── */}
        {tab === 'ebooks' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b"><h2 className="font-semibold text-wa-text">Ebook</h2></div>
            <Toolbar filtered={fEbooks} exportName="ebook"
              exportHeaders={['Judul', 'Harga', 'Diskon', 'Status']}
              exportMap={(e) => ({ 'Judul': e.title, 'Harga': e.price, 'Diskon': e.discount_pct ? e.discount_pct + '%' : '', 'Status': e.is_active ? 'Aktif' : 'Nonaktif' })} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="p-3">Cover</th><th className="p-3">Judul</th><th className="p-3">Harga</th><th className="p-3">Diskon</th><th className="p-3">Status</th><th className="p-3">Aksi</th>
                </tr></thead>
                <tbody>
                  {paginate(fEbooks).map((e) => (
                    <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-3">
                        {e.cover_url && <img src={mediaUrl(e.cover_url)} alt="" className="w-8 h-10 rounded object-cover" onError={(ev) => ev.target.style.display = 'none'} />}
                      </td>
                      <td className="p-3 font-medium max-w-[200px] truncate">{e.title}</td>
                      <td className="p-3">{fmtRp(e.price)}</td>
                      <td className="p-3">
                        {e.discount_pct > 0 ? <span className="text-red-500">{e.discount_pct}%</span> : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="p-3"><span className={`text-xs ${e.is_active ? 'text-green-600' : 'text-gray-400'}`}>{e.is_active ? 'Aktif' : 'Nonaktif'}</span></td>
                      <td className="p-3"><button onClick={() => openEdit('ebook', e)} className={`p-1 ${e.is_active ? 'text-green-600 hover:text-red-500' : 'text-gray-400 hover:text-green-600'}`} title={e.is_active ? 'Nonaktifkan' : 'Aktifkan'}><PowerIcon className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fEbooks.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Tidak ada ebook</p>}
            </div>
            <Paging total={fEbooks.length} />
          </div>
        )}

        {/* ── VOUCHERS ── */}
        {tab === 'vouchers' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-wa-text">Voucher</h2>
              <button onClick={() => { setEditingVoucher(null); setVoucherForm({ ...EMPTY_VOUCHER }); setShowVoucherForm(true); }}
                className="bg-wa-dark text-white text-sm px-4 py-2 rounded-lg hover:bg-green-900 transition">
                + Buat Voucher
              </button>
            </div>
            <Toolbar filtered={fVouchers} exportName="voucher"
              exportHeaders={['Kode', 'Tipe', 'Nilai', 'Min Beli', 'Pemakaian', 'Status']}
              exportMap={(v) => ({ 'Kode': v.code, 'Tipe': v.type, 'Nilai': v.value, 'Min Beli': v.min_amount, 'Pemakaian': v.used_count || 0, 'Status': v.is_active ? 'Aktif' : 'Nonaktif' })} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="p-3">Kode</th><th className="p-3">Tipe</th><th className="p-3">Nilai</th><th className="p-3">Min Beli</th><th className="p-3">Pemakaian</th><th className="p-3">Status</th><th className="p-3">Aksi</th>
                </tr></thead>
                <tbody>
                  {paginate(fVouchers).map((v) => (
                    <tr key={v.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-3 font-mono font-semibold">{v.code}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${v.type === 'percent' ? 'bg-purple-50 text-purple-600' : 'bg-orange-50 text-orange-600'}`}>{v.type === 'percent' ? 'Persen' : 'Nominal'}</span></td>
                      <td className="p-3">{v.type === 'percent' ? `${v.value}%` : fmtRp(v.value)}</td>
                      <td className="p-3">{v.min_amount > 0 ? fmtRp(v.min_amount) : '-'}</td>
                      <td className="p-3">{v.used_count || 0}{v.max_uses > 0 ? ` / ${v.max_uses}` : ''}</td>
                      <td className="p-3">
                        <button onClick={() => toggleVoucher(v.id)}
                          className={`text-xs px-2 py-1 rounded-full ${v.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                          {v.is_active ? 'Aktif' : 'Nonaktif'}
                        </button>
                      </td>
                      <td className="p-3 flex gap-1">
                        <button onClick={() => openEditVoucher(v)} className="text-wa-dark hover:text-wa-dark p-1"><PencilIcon className="w-4 h-4" /></button>
                        <TrashBtn type="voucher" id={v.id} label={v.code} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fVouchers.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Belum ada voucher</p>}
            </div>
            <Paging total={fVouchers.length} />
          </div>
        )}
      </div>

      {/* ── VOUCHER FORM MODAL ── */}
      {showVoucherForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowVoucherForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-wa-text mb-4">{editingVoucher ? 'Edit' : 'Buat'} Voucher</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Kode Voucher *</label>
                <input value={voucherForm.code} onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })}
                  disabled={!!editingVoucher} placeholder="CONTOH20"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm uppercase disabled:bg-gray-100" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Tipe *</label>
                  <select value={voucherForm.type} onChange={(e) => setVoucherForm({ ...voucherForm, type: e.target.value })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-white">
                    <option value="percent">Persen (%)</option>
                    <option value="fixed">Nominal (Rp)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Nilai *</label>
                  <input type="number" value={voucherForm.value} onChange={(e) => setVoucherForm({ ...voucherForm, value: e.target.value })}
                    placeholder={voucherForm.type === 'percent' ? '20' : '5000'} min="1"
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Minimum Pembelian (Rp)</label>
                <input type="number" value={voucherForm.min_amount} onChange={(e) => setVoucherForm({ ...voucherForm, min_amount: e.target.value })}
                  placeholder="0 = tanpa minimum" min="0"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Batas Pemakaian</label>
                <input type="number" value={voucherForm.max_uses} onChange={(e) => setVoucherForm({ ...voucherForm, max_uses: e.target.value })}
                  placeholder="0 = tanpa batas" min="0"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Deskripsi</label>
                <input value={voucherForm.description} onChange={(e) => setVoucherForm({ ...voucherForm, description: e.target.value })}
                  placeholder="Contoh: Diskon 20% untuk semua ebook"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Berlaku Dari</label>
                  <input type="date" value={voucherForm.valid_from} onChange={(e) => setVoucherForm({ ...voucherForm, valid_from: e.target.value })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Berlaku Sampai</label>
                  <input type="date" value={voucherForm.valid_until} onChange={(e) => setVoucherForm({ ...voucherForm, valid_until: e.target.value })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setShowVoucherForm(false); setEditingVoucher(null); }} className="flex-1 border border-gray-200 rounded-lg py-2 text-sm text-gray-600 hover:bg-gray-50">Batal</button>
              <button onClick={saveVoucher} className="flex-1 bg-wa-dark text-white rounded-lg py-2 text-sm hover:opacity-90">Simpan</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'affiliate' && <AffiliateAdminPanel />}

      {/* ── EDIT MODAL ── */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-wa-text mb-4">
              Edit {editing.type === 'user' ? 'Pengguna' : editing.type === 'tenant' ? 'Tenant' : 'Order'}
            </h3>
            {editing.type === 'user' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Nama</label>
                  <input value={editing.data.name || ''} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Email</label>
                  <input value={editing.data.email || ''} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, email: e.target.value } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Role</label>
                  <select value={editing.data.role || ''} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, role: e.target.value } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-white">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
            )}
            {editing.type === 'tenant' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Nama</label>
                  <input value={editing.data.name || ''} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Plan</label>
                  <select value={editing.data.plan || 'free'} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, plan: e.target.value } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-white">
                    <option value="free">free</option>
                    <option value="premium">premium</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Status</label>
                  <select value={editing.data.is_active ? '1' : '0'} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, is_active: e.target.value === '1' } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-white">
                    <option value="1">Aktif</option>
                    <option value="0">Nonaktif</option>
                  </select>
                </div>
              </div>
            )}
            {editing.type === 'order' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Status</label>
                  <select value={editing.data.status || 'pending'} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, status: e.target.value } })}
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-white">
                    {['pending','paid','failed','expired'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="flex-1 border border-gray-200 rounded-lg py-2 text-sm text-gray-600 hover:bg-gray-50">Batal</button>
              <button onClick={saveEdit} className="flex-1 bg-wa-dark text-white rounded-lg py-2 text-sm hover:opacity-90">Simpan</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM ── */}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDel(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-wa-text mb-2">Hapus?</h3>
            <p className="text-sm text-gray-500 mb-4">Yakin hapus <b>{confirmDel.label}</b>?</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDel(null)} className="flex-1 border border-gray-200 rounded-lg py-2 text-sm text-gray-600 hover:bg-gray-50">Batal</button>
              <button onClick={doDelete} className="flex-1 bg-red-500 text-white rounded-lg py-2 text-sm hover:bg-red-600">Hapus</button>
            </div>
          </div>
        </div>
)}
    </div>
  );
}

const AFF_STATUS_COLOR = { active: 'text-green-600', pending: 'text-yellow-600', suspended: 'text-red-500', inactive: 'text-gray-400' };
const AFF_COMM_COLOR = { pending: 'text-yellow-600', available: 'text-green-600', paid: 'text-blue-600', cancelled: 'text-gray-400' };
const fmtRpA = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

function AffiliateAdminPanel() {
  const [aTab, setATab] = useState('affiliates');
  const [affiliates, setAffiliates] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({});
  const [pkgEdit, setPkgEdit] = useState({});

  const load = () => {
    api.get('/api/affiliate/admin/affiliates').then(r => setAffiliates(r.data || [])).catch(() => {});
    api.get('/api/affiliate/admin/ebooks').then(r => setEbooks(r.data || [])).catch(() => {});
    api.get('/api/affiliate/admin/commissions').then(r => setCommissions(r.data || [])).catch(() => {});
    api.get('/api/affiliate/admin/withdrawals').then(r => setWithdrawals(r.data || [])).catch(() => {});
    api.get('/api/affiliate/admin/stats').then(r => setStats(r.data || {})).catch(() => {});
  };
  useEffect(load, []);

  const setAffStatus = async (id, status) => {
    try { await api.put(`/api/affiliate/admin/affiliates/${id}/status`, { status }); toast.success('Status diperbarui'); load(); }
    catch { toast.error('Gagal'); }
  };

  const saveEbookAff = async (ebookId) => {
    const d = pkgEdit[ebookId] || {};
    try { await api.put(`/api/affiliate/admin/ebooks/${ebookId}/affiliate`, { affiliate_enabled: !!d.affiliate_enabled, commission_type: d.commission_type || 'percent', commission_value: parseFloat(d.commission_value) || 0 }); toast.success('Disimpan'); load(); }
    catch { toast.error('Gagal'); }
  };

  const setWdStatus = async (id, status) => {
    const note = status === 'rejected' ? prompt('Alasan penolakan:') : null;
    try { await api.put(`/api/affiliate/admin/withdrawals/${id}/status`, { status, note }); toast.success('Status diperbarui'); load(); }
    catch { toast.error('Gagal'); }
  };

  const ATABS = ['affiliates', 'ebooks', 'commissions', 'withdrawals'];
  const ATAB_LABEL = { affiliates: 'Affiliates', ebooks: 'Produk', commissions: 'Komisi', withdrawals: 'Penarikan' };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Total Affiliate', stats.total_affiliates], ['Aktif', stats.active_affiliates], ['Total Klik', stats.total_clicks], ['Komisi Pending', fmtRpA(stats.commissions_pending)]].map(([label, val]) => (
          <div key={label} className="bg-white rounded-xl shadow-sm p-4"><p className="text-xs text-gray-500">{label}</p><p className="text-xl font-bold text-wa-text">{val ?? 0}</p></div>
        ))}
      </div>
      <div className="flex gap-1 border-b">
        {ATABS.map(k => <button key={k} onClick={() => setATab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${aTab === k ? 'border-wa-dark text-wa-dark' : 'border-transparent text-gray-500'}`}>{ATAB_LABEL[k]}</button>)}
      </div>

      {aTab === 'affiliates' && (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b">
            <th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Daftar</th><th className="p-3">Aksi</th>
          </tr></thead><tbody>
            {affiliates.map(a => (<tr key={a.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="p-3">{a.user_name || '-'}</td>
              <td className="p-3">{a.user_email || '-'}</td>
              <td className="p-3"><span className={AFF_STATUS_COLOR[a.status] || 'text-gray-400'}>{a.status}</span></td>
              <td className="p-3">{a.created_at ? new Date(a.created_at).toLocaleDateString('id-ID') : '-'}</td>
              <td className="p-3"><select value={a.status} onChange={e => setAffStatus(a.id, e.target.value)} className="border border-gray-200 rounded px-2 py-1 text-xs bg-white">
                {['pending','active','suspended','inactive'].map(s => <option key={s} value={s}>{s}</option>)}
              </select></td>
            </tr>))}
          </tbody></table>
        </div>
      )}

      {aTab === 'ebooks' && (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b">
            <th className="p-3">Judul</th><th className="p-3">Harga</th><th className="p-3">Affiliate</th><th className="p-3">Tipe</th><th className="p-3">Nilai</th><th className="p-3">Aksi</th>
          </tr></thead><tbody>
            {ebooks.map(e => {
              const ed = pkgEdit[e.id] || { affiliate_enabled: e.affiliate_enabled, commission_type: e.commission_type || 'percent', commission_value: e.commission_value || 0 };
              return (<tr key={e.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="p-3">{e.title}</td>
                <td className="p-3">{fmtRpA(e.final_price || e.price)}</td>
                <td className="p-3"><input type="checkbox" checked={!!ed.affiliate_enabled} onChange={ev => setPkgEdit(p => ({...p, [e.id]: {...ed, affiliate_enabled: ev.target.checked}}))} /></td>
                <td className="p-3"><select value={ed.commission_type} onChange={ev => setPkgEdit(p => ({...p, [e.id]: {...ed, commission_type: ev.target.value}}))} className="border border-gray-200 rounded px-2 py-1 text-xs bg-white"><option value="percent">%</option><option value="nominal">Rp</option></select></td>
                <td className="p-3"><input type="number" value={ed.commission_value} onChange={ev => setPkgEdit(p => ({...p, [e.id]: {...ed, commission_value: ev.target.value}}))} className="border border-gray-200 rounded px-2 py-1 text-xs w-24" /></td>
                <td className="p-3"><button onClick={() => saveEbookAff(e.id)} className="bg-wa-dark text-white text-xs px-3 py-1 rounded-lg">Simpan</button></td>
              </tr>);
            })}
          </tbody></table>
        </div>
      )}

      {aTab === 'commissions' && (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b">
            <th className="p-3">Affiliate</th><th className="p-3">Ebook</th><th className="p-3">Jumlah</th><th className="p-3">Status</th><th className="p-3">Tanggal</th>
          </tr></thead><tbody>
            {commissions.map(c => (<tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="p-3">{c.affiliate_user_name || '-'}</td>
              <td className="p-3">{c.ebook_title || '-'}</td>
              <td className="p-3 font-medium">{fmtRpA(c.commission_amount)}</td>
              <td className="p-3"><span className={AFF_COMM_COLOR[c.status] || 'text-gray-400'}>{c.status}</span></td>
              <td className="p-3">{c.created_at ? new Date(c.created_at).toLocaleDateString('id-ID') : '-'}</td>
            </tr>))}
          </tbody></table>
        </div>
      )}

      {aTab === 'withdrawals' && (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b">
            <th className="p-3">Nama</th><th className="p-3">Bank</th><th className="p-3">No Rek</th><th className="p-3">Jumlah</th><th className="p-3">Status</th><th className="p-3">Aksi</th>
          </tr></thead><tbody>
            {withdrawals.map(w => (<tr key={w.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="p-3">{w.user_name || '-'}</td>
              <td className="p-3">{w.bank_name || '-'}</td>
              <td className="p-3">{w.bank_account_number || '-'}</td>
              <td className="p-3 font-medium">{fmtRpA(w.amount)}</td>
              <td className="p-3">{w.status}</td>
              <td className="p-3 flex gap-1">
                {w.status === 'pending' && <button onClick={() => setWdStatus(w.id, 'approved')} className="bg-green-600 text-white text-xs px-2 py-1 rounded">Approve</button>}
                {w.status === 'approved' && <button onClick={() => setWdStatus(w.id, 'paid')} className="bg-blue-600 text-white text-xs px-2 py-1 rounded">Paid</button>}
                {['pending','approved'].includes(w.status) && <button onClick={() => setWdStatus(w.id, 'rejected')} className="bg-red-500 text-white text-xs px-2 py-1 rounded">Tolak</button>}
              </td>
            </tr>))}
          </tbody></table>
        </div>
      )}
    </div>
  );
}
